import React, { useState } from 'react';
import { Eye, User, Mail, Phone, Building, FileText, Calendar, Hash, Globe, DollarSign, Check, X, Send, Home, FileSignature } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';
import { sendEmail } from '../../services/emailService';
import toast from 'react-hot-toast';

export default function EquipmentLeases() {
  const { data, refetch } = useSupabase(abvigAPI.getEquipmentLeases);
  const [selectedLease, setSelectedLease] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const handleView = (item) => {
    setSelectedLease(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
    setEmailSubject(`Regarding Your Equipment Lease - ${item.lease_ref || 'Application'}`);
    setEmailMessage(`Dear ${item.lessee_name || 'Sir/Madam'},

Thank you for your equipment lease application. We have received your request and will review it shortly.

Best regards,
ABVIG Team`);
  };

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await abvigAPI.updateEquipmentLeaseStatus(id, status);
      toast.success(`Lease ${status} successfully`);
      refetch();
      if (isModalOpen) {
        setIsModalOpen(false);
        setSelectedLease(null);
        setShowEmailForm(false);
      }
    } catch (error) {
      toast.error(`Failed to ${status} lease`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedLease) return;
    
    setIsSendingEmail(true);
    try {
      await sendEmail({
        to: selectedLease.lessee_email,
        subject: emailSubject,
        message: emailMessage,
        fromName: process.env.REACT_APP_SENDER_NAME || 'ABVIG Team',
      });
      
      toast.success(`✅ Email sent to ${selectedLease.lessee_email}`);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const renderLeaseDetails = (lease) => {
    if (!lease) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isSendingEmail}
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'ABVIG Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'info@abvig.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{lease.lessee_email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isSendingEmail}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your message here..."
              disabled={isSendingEmail}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailMessage}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSendingEmail}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Equipment Lease Agreement</h3>
                <p className="text-sm text-gray-600">Reference: {lease.lease_ref || 'N/A'}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`badge ${getStatusColor(lease.status)} text-sm`}>
                {getStatusLabel(lease.status)}
              </span>
              <p className="text-xs text-gray-500 mt-1">Submitted: {formatDate(lease.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Lessee Information */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4" />
              Lessee Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Lessee Name</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Registration No.</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_reg_no || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Country</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_country || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">TIN</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_tin || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Representative</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_representative || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessee_phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lessor Information */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Lessor Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Lessor Name</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Registration No.</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_reg_no || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Country</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_country || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">TIN</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_tin || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Representative</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_representative || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{lease.lessor_phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lease Details */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileSignature className="w-4 h-4" />
              Lease Details
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Lease Type</p>
                <p className="text-sm font-medium text-gray-900">{lease.lease_type || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Currency</p>
                <p className="text-sm font-medium text-gray-900">{lease.currency || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Lease Term</p>
                <p className="text-sm font-medium text-gray-900">{lease.lease_term || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Rental Amount</p>
                <p className="text-sm font-medium text-gray-900">{lease.rental_amount || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Governing Law</p>
                <p className="text-sm font-medium text-gray-900">{lease.governing_law || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowEmailForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Send className="w-4 h-4" />
            Respond with Email
          </button>

          {lease.status === 'pending' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleStatusUpdate(lease.id, 'approved')}
                disabled={processingId === lease.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {processingId === lease.id ? 'Processing...' : 'Approve Lease'}
              </button>
              <button
                onClick={() => handleStatusUpdate(lease.id, 'rejected')}
                disabled={processingId === lease.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                {processingId === lease.id ? 'Processing...' : 'Reject Lease'}
              </button>
            </div>
          )}

          {lease.status !== 'pending' && (
            <div className="pt-2">
              <p className="text-sm text-gray-500 text-center">
                This lease has been <span className={`font-semibold ${lease.status === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                  {getStatusLabel(lease.status)}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'lease_ref', label: 'Reference', sortable: true },
    { key: 'lessee_name', label: 'Lessee Name', sortable: true },
    { key: 'lessee_email', label: 'Email' },
    { key: 'lease_type', label: 'Lease Type' },
    { key: 'lease_term', label: 'Term' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Date',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Equipment Leases</h1>
        <p className="text-gray-600">Manage equipment lease agreements</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} leases`}
        actions={actions}
        searchPlaceholder="Search leases..."
        onRowClick={(item) => handleView(item)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedLease(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Email" : "Lease Details"}
        size="max-w-3xl"
      >
        {renderLeaseDetails(selectedLease)}
      </Modal>
    </div>
  );
}